import { alpha, type Theme } from '@mui/material/styles';
import type { ToggleButtonProps } from '@mui/material/ToggleButton';
import { Colour } from '@tyro/api';

const COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
  'indigo',
  ...Object.values(Colour),
  'slate',
  'gray',
] as const;

// Gap between each grouped button and the group's outer edge. Added to the shape
// radius below so the group's corner stays concentric with the buttons inside it.
const groupedMargin = 4;

export default function ToggleButton(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyle = (ownerState: ToggleButtonProps) => {
    const standardColor = ownerState.color === 'standard';

    const defaultStyle = {
      textTransform: 'none',
      color: theme.palette.slate[400],
      padding: '4px 10px',
      ...(standardColor && {
        '&.Mui-selected': {
          borderColor: 'inherit',
        },
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        '&:hover': {
          borderColor: alpha(theme.palette[color].main, 0.48),
          backgroundColor: alpha(
            theme.palette[color].main,
            theme.palette.action.hoverOpacity,
          ),
        },
        '&.Mui-selected': {
          borderColor: theme.palette[color].main,
          color: theme.palette[color][isLight ? 'main' : 'light'],
          backgroundColor: theme.palette[color][isLight ? 'lighter' : 'darker'],
        },
      }),
    }));

    const disabledState = {
      '&.Mui-disabled': {
        '&.Mui-selected': {
          color: theme.palette.action.disabled,
          backgroundColor: theme.palette.action.selected,
          borderColor: theme.palette.action.disabledBackground,
        },
      },
    };

    return [...colorStyle, defaultStyle, disabledState];
  };

  return {
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ToggleButtonProps }) =>
          rootStyle(ownerState),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          // Keep the outer corner concentric with the inner buttons: each grouped
          // button keeps the shape radius and sits inside a `groupedMargin` gap, so the
          // group radius is the sum of the two and the rounding runs smoothly to the edge.
          borderRadius: Number(theme.shape.borderRadius) + groupedMargin,
          backgroundColor: theme.palette.background.paper,
          border: `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
        },
        grouped: {
          margin: groupedMargin,
          borderColor: 'transparent !important',
          borderRadius: `${theme.shape.borderRadius}px !important`,
        },
      },
    },
  };
}
